import { Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { HomeworksService } from '@/homework/services/homeworks.service';
import { AuthenticatedFileDownloadProvider } from '@/shared/providers/authenticated-file-download.provider';
import EdInputModel from '@/models/ed-input.model';
import KEYS from '@/values/keys';

@Component({ name: 'ed-consolidate-homeworks'})
export default class EdConsolidateHomeworks extends Vue {

  @Prop() readonly params: any;
  
  /* Refs */
  @Ref('table') readonly dataTable: any;

  public students: any[] = [];
  public loading: boolean = false;
  private reloadTimeout = null;
  public errorMessage : any = '';

  public selectParams : boolean = false;
  public reportParams = {
          studentId : 0,
          includeFeedback : EdInputModel('includeFeedback', false, {}, true),
          includeProblem : EdInputModel('includeProblem', false, {}, true),
  }

  public showDataTable: boolean = false;

  get classInfo() {
    return this.params.classInfo;
  }

  get content() {
    return this.params.content;
  }

  get consolidateHomeworksTitle() {
    return this.$t('classes.consolidatedHomeworksTitle', { className: this.classInfo.name, subjectName: this.content.contentTitle })
  }
  
  get  headers() { 
    return [
      { text: this.$t('classes.headers.students'), width: '60%', align: 'start', value: 'name', sortable: false},
      { text: this.$t('common.date'),  width: '30%',  value: 'date', sortable: false},
      { text: this.$t('classes.headers.actions'), width: '10%' , value: 'actions',  sortable: false},
      ]
  }

  get studentsList(): any[] {
    return this.students;
  }

  public closeConsolidatedHomeworks(){
    this.$EdModal.closeModal();
  }

  public getConsolidatedHomeworks() {
    this.loading = true;
    let reload = false;
    HomeworksService.getConsolidatedHomeworksByClass(this.classInfo.id, this.content.contentId)
      .then((res) => { 
        this.loading = false;
        this.students = res;
        res.forEach(st => {
          if(st.status === 'PROCESSING') reload = true;
        });
        if(reload) 
          this.reloadWhileProcessing();
      }).catch((err) => {
        this.loading = false;
        this.error(this.$t('errors.errorLoading'))
      })
  }

  public generate(item : any){
    this.reportParams = {
      studentId : item.student.id,
      includeFeedback : EdInputModel('includeFeedback', false, {}, true),
      includeProblem : EdInputModel('includeProblem', false, {}, true),
    }
    this.selectParams = true;
  }

  public doGenerate(){
      this.selectParams = false;
      let entry = this.students.find(st => st.student.id ===  this.reportParams.studentId)
      entry.status  = 'PROCESSING'
      entry.allowsRun = false;
      HomeworksService.createConsolidatedHomeworks(this.classInfo.id, 
        this.content.contentId, 
        this.reportParams.studentId, 
        this.reportParams.includeFeedback.value,
        this.reportParams.includeProblem.value)
      .then((res) => { 
        this.selectParams = false;
        this.reloadWhileProcessing();
      }).catch((err) => {
        this.error( this.$t('errors.errorSaving'))
      })
  }

  private reloadWhileProcessing(){
    if(this.reloadTimeout) //When there is a reload pendind, stop it
      clearTimeout(this.reloadTimeout)
    
    this.reloadTimeout = setTimeout(() => {
      this.getConsolidatedHomeworks();
      this.reloadTimeout = null
    }, 3000);
  }

  public async download(item : any){
    const separator = item.assetInfo.url.includes('?') ? '&' : '?';
    await AuthenticatedFileDownloadProvider.download(
      `${item.assetInfo.url}${separator}attachment=true`,
      item.assetInfo.fileName || 'consolidated-homeworks.zip'
    );
  }

  private error(message : any){
    this.errorMessage = message;
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('common.error'),
      body: this.errorMessage,
      type: KEYS.EVENT_TYPES.DANGER
    });
  }

  private _loadEmptyDataTable(): void {
    this.dataTable?.loadData({ items: [], headers: this.headers });
  }

  private _loadDataTable(): void {

    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.studentsList || this.studentsList.length == 0) {
      this._loadEmptyDataTable();
      return;
    }

    function _getActionExpression(target: string, expression: Function): any {
      return {
          target: target, attribute: 'class', booleanAttribute: false, concat: true,
          expression: expression, applied: 'shown', fallback: 'hidden',
      };
    }

    const slots = {
      'item.name': {
        template: document.querySelector('#item\\.name').innerHTML,
        templateParams: [
          { key: 'item.name',  value: (params: any) => params.item.student.fullName },
          { key: 'title.name', value: (params: any) => params.item.student.fullName },
        ],
      },
      'item.date': {
        template: document.querySelector('#item\\.date').innerHTML,
        templateParams: [{ key: 'item.date', value: (params: any) => params.item.date ?? '-' }],
      },
      'item.actions': {
        template: document.querySelector('#item\\.actions').innerHTML,
        expressions: [
          _getActionExpression('.generate',         (params: any) => params.item.allowsRun),
          _getActionExpression('.download',         (params: any) => params.item.status === 'READY'),
          _getActionExpression('.processing',       (params: any) => params.item.status === 'PROCESSING'),
          _getActionExpression('.warning-no-data',  (params: any) => params.item.status === 'NO_DATA'),
          _getActionExpression('.status-error',     (params: any) => params.item.status === 'ERROR'),
        ],
        events: [
          { target: '.event-generate', handler: (params) =>  params.parent.generate(params.item) },
          { target: '.event-download', handler: (params) =>  params.parent.download(params.item) },
        ]
      },
      parent: this,
    };

    this.dataTable.loadData({
      headers: this.headers,
      items: this.studentsList,
      totalItems: this.studentsList.length,
      slots: slots
    });
  }

  private _initDataTable(): void {
    setTimeout(() => {
      this.showDataTable = true;
      this._loadEmptyDataTable();
      this.getConsolidatedHomeworks();
    }, 500); // Timeout to wait for the modal to be ready
  }

  @Watch('studentsList', { immediate: true, deep: true })
  private _watchStudentsList(): void {
    this._loadDataTable();
  }

  /* Hooks */
  mounted() {
    this._initDataTable();
  }

  beforeDestroy(){
    if (this.reloadTimeout) clearTimeout(this.reloadTimeout);
    
  }
}
